import ReactGA from 'react-ga4';

import { runWhenIdle } from './idle';

let currentLocation;

if (inBrowser()) {
  // Deferred: initializing immediately (as an import-time side effect) competed with the app's
  // own startup work for the critical rendering path; analytics doesn't need to be that early.
  runWhenIdle(() => ReactGA.initialize('G-QHVWQC05JS'));
}

export function inBrowser() {
  return !(
    typeof document === 'undefined' ||
    (window.process && window.process.title.includes('node')) ||
    (window.process && window.process.title.includes('test')) ||
    (window.process && window.process.env.JEST_WORKER_ID !== undefined)
  );
}

export function logAnalytics() {
  if (inBrowser() && window.location.pathname !== currentLocation) {
    currentLocation = window.location.pathname;
    ReactGA.send({
      hitType: 'pageview',
      page: currentLocation
    });
  }
}

export function transformHistory(history, func) {
  if (history && history.location) {
    const currentPath = history.location.pathname;
    const newPath = func(currentPath === '/' ? '/compareClips' : currentPath);
    history.push(newPath);
  }
}

export function getHash(history) {
  return history && history.location.hash.split('#')[1];
}

export function setHash(history, hash) {
  transformHistory(history, path => `${path}#${hash}`);
}

// Checks whether an ad actually rendered, rather than whether adsbygoogle.js's own onload fired -
// most real ad blockers deliberately let that base script load (since "did onload fire" is the
// naive check many sites use) while blocking the deeper per-slot ad-fetch requests it makes
// afterward, which would make the old check always report "not blocked" even when it was.
// "Filled" here means at least one of our ad slots got real content - data-ad-status="filled" is
// the attribute AdSense itself sets once a slot resolves. Deliberately NOT also checking for an
// inserted iframe as a fallback: confirmed via real production data that an "unfilled" slot can
// still have an iframe (Google's own placeholder plumbing), so that signal alone is unreliable.
export function areAdsInvisible() {
  const adSlots = document.querySelectorAll('ins.adsbygoogle');
  if (adSlots.length === 0) {
    return true;
  }
  return !Array.prototype.some.call(
    adSlots,
    slot => slot.getAttribute('data-ad-status') === 'filled'
  );
}

// https://stackoverflow.com/a/38241481
export function getOS() {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  let os = platform;

  if (macosPlatforms.includes(platform)) {
    os = 'Mac OS';
  } else if (iosPlatforms.includes(platform)) {
    os = 'iOS';
  } else if (windowsPlatforms.includes(platform)) {
    os = 'Windows';
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
  } else if (/Linux/.test(platform)) {
    os = 'Linux';
  }

  return os;
}

export function canUseExamDiff() {
  return getOS() === 'Windows';
}

// Same setups and detection as setAutoEdpDownloadLinks in the PS site's scripts.js; update both
// when the version changes
const edpSetups = {
  x64: 'edpro170_x64_setup.exe',
  arm64: 'edpro170_arm64_setup.exe',
  x86: 'edpro170_Win32_setup.exe'
};

function setupFromUserAgent() {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('win64') || userAgent.includes('x64')) {
    return edpSetups.x64;
  }
  return userAgent.includes('win32') || userAgent.includes('wow32') ? edpSetups.x86 : edpSetups.x64;
}

async function detectEdpSetup() {
  if (!navigator.userAgentData) {
    return setupFromUserAgent();
  }
  try {
    const { architecture, bitness } = await navigator.userAgentData.getHighEntropyValues([
      'architecture',
      'bitness'
    ]);
    if (architecture === 'arm' && bitness === '64') {
      return edpSetups.arm64;
    }
    if (architecture === 'x86' && bitness === '32') {
      return edpSetups.x86;
    }
    return edpSetups.x64;
  } catch (e) {
    return setupFromUserAgent();
  }
}

// Direct ExamDiff Pro download for this PC; the PS page it opens starts the download
export async function edpDownloadUrl(ref) {
  const setup = await detectEdpSetup();
  return `https://www.prestosoft.com/ps_download.asp?file=${setup}&prod=edp&ref=${ref}`;
}
